import {
  toWSDateString,
  WSActions,
  WSButton,
  WSCard,
  WSElement,
  WSFlexBox,
  WSInfoBox,
  WSText,
  WSTimeline,
  WSTimelineItemProps
} from "@wingspanhq/fe-component-library";
import type { resourceEventHistoryApiSpec } from "@wingspanhq/wingspan-api-types";
import startCase from "lodash/startCase";
import { useCallback, useMemo } from "react";
import { useQuerylistResourceEvents } from "../../../query/resourceEvents/queries/useQueryListEvents";
import { eventGroupToTimeline } from "./eventGroupToTimeline";
import { groupEvents } from "./groupEvents";
import { mapEventTitle } from "./mapEventTitle";
import { useModalResourceDrillDownHistory } from "./ModalResourceDrillDownHistory";

type Props = {
  refetchOnMount?: boolean;
  resources: resourceEventHistoryApiSpec.IEventResource[];
  onViewAll?: () => void;
  drillDownResourceType?: string;
  bare?: boolean;
};

const NotBackfilledResourceTypes = [
  "Engagement",
  "EngagementRequirementDefinition",
  "RequirementDefinition",
  "Payer",
  "Payee",
  "PayerPayeeEngagement",
  "Requirement"
];

const HISTORY_TRACKING_START_DATE = "7-11-2025";

export const ResourceHistory: React.FC<Props> = props => {
  const refetchOnMount = props.refetchOnMount ?? true;
  const modalDrillDown = useModalResourceDrillDownHistory();

  const handleDrillDown = useCallback(
    (resource: resourceEventHistoryApiSpec.IEventResource) => {
      modalDrillDown.open({
        resource,
        title: `${startCase(resource.resourceType)} history`
      });
    },
    [modalDrillDown]
  );

  const queryResourceEvents = useQuerylistResourceEvents(
    {
      filter: {
        resource: {
          data: props.resources
        }
      },
      "page[size]": props.onViewAll ? 4 : 100
    },
    {
      refetchOnMount
    }
  );

  const showNotAllBackfilledNote = useMemo(
    () =>
      props.resources.some(resource =>
        NotBackfilledResourceTypes.includes(resource.resourceType)
      ),
    [props.resources]
  );

  const eventGroups = useMemo(
    () =>
      groupEvents(
        (queryResourceEvents.data ?? []).map(event => ({
          ...event,
          title: mapEventTitle(event.title)
        }))
      ),
    [queryResourceEvents.data]
  );

  const timelineItems = useMemo<WSTimelineItemProps[]>(() => {
    const items = eventGroups.map(group =>
      eventGroupToTimeline(
        group,
        !!props.onViewAll,
        props.drillDownResourceType,
        props.drillDownResourceType ? handleDrillDown : undefined
      )
    );

    if (items.length === 4 && props.onViewAll) {
      return [
        ...items,
        {
          content: (
            <WSButton.Link onClick={props.onViewAll}>View all</WSButton.Link>
          ),
          avatar: {
            type: "icon",
            icon: "time"
          }
        }
      ];
    }

    return items;
  }, [
    eventGroups,
    props.onViewAll,
    props.drillDownResourceType,
    handleDrillDown
  ]);

  const content = queryResourceEvents.isSuccess ? (
    timelineItems.length ? (
      <WSTimeline items={timelineItems} />
    ) : (
      <WSFlexBox.Center p="L">
        <WSText.ParagraphSm color="gray400">No events yet</WSText.ParagraphSm>
      </WSFlexBox.Center>
    )
  ) : (
    <WSElement shimmer pb="2XL">
      Loading history...
    </WSElement>
  );

  const backfillNote = showNotAllBackfilledNote ? (
    <WSInfoBox mt="M">
      <WSText.ParagraphXs color="gray500">
        Note: some history events tracking is only available from this date
        onward: {toWSDateString(HISTORY_TRACKING_START_DATE, "monthDayYear")}.
      </WSText.ParagraphXs>
    </WSInfoBox>
  ) : null;

  if (props.bare) {
    return (
      <WSElement>
        {content}
        {backfillNote}
      </WSElement>
    );
  }

  return (
    <WSElement>
      <WSFlexBox
        alignItems="center"
        justify="space-between"
        wrap="nowrap"
        mb="M"
      >
        <WSText weight="medium">
          {props.onViewAll ? "Recent history" : "History"}
        </WSText>
        {props.onViewAll &&
          queryResourceEvents.isSuccess &&
          timelineItems.length > 0 && (
            <WSActions
              buttons={[
                { kind: "Link", label: "View all", onClick: props.onViewAll }
              ]}
            />
          )}
      </WSFlexBox>

      <WSCard>
        {content}
        {backfillNote}
      </WSCard>
    </WSElement>
  );
};
